import { type ReactNode, useLayoutEffect, useState } from 'react'

import { Box, Text } from '@enechain/ecloud-designsystem'

import { pinAreaAndPeriodFirst } from '~/components/common/filters/filterItemOrder'

/** 条件 1 つ = ラベル + 部品。ラベルの無いもの (状態のピル等) は部品だけでマスを使う。 */
export type FilterGridItem = {
  /** 並べ替え (`pinAreaAndPeriodFirst`) の判定にも使う。エリアは `area`。 */
  key: string
  label?: ReactNode
  control: ReactNode
}

/** ラベル → 部品の間。 */
const LABEL_GAP_PX = 8
/** 列の間 (縦線は置かない)。 */
const COLUMN_GAP_PX = 20
/** 行の間 (UI 辞書 toolbar-collapsible.md「開いた条件のカード」12px)。 */
const ROW_GAP_PX = 12
/** 1 マスの部品の最小幅。これより狭くなる列数は選ばない。 */
const MIN_CONTROL_WIDTH_PX = 200
/** これより低い要素 (隠した input・読み上げ用の 1px のラベルなど) は部品の 1 行目に数えない。 */
const MIN_FIRST_LINE_PX = 12
/** これより高い要素は「1 行目」ではなく折り返した部品全体として扱う。 */
const MAX_FIRST_LINE_PX = 48

/**
 * 部品の 1 行目の高さ = 部品の上端にそろっている最初の要素の高さ (40px の select・日付の送り、
 * 24px のチェックボックス・ラジオ)。折り返した部品 (日付のまとまりの下の当日 / 翌日、発注主体の
 * 下の発注できるエリアの文) は 1 行目だけを見る。上端は部品の器の上の余白 (行の中央へ寄せる余白。
 * `alignGridRows`) の内側で見る。React mock の FilterGrid と同じ求め方。
 */
const firstLineHeight = (control: HTMLElement): number => {
  const top =
    control.getBoundingClientRect().top +
    (parseFloat(getComputedStyle(control).paddingTop) || 0)
  for (const element of control.querySelectorAll<HTMLElement>('*')) {
    const rect = element.getBoundingClientRect()
    if (rect.height < MIN_FIRST_LINE_PX || rect.height > MAX_FIRST_LINE_PX)
      continue
    if (Math.abs(rect.top - top) <= 1) return rect.height
  }
  return 0
}

/** 部品の 1 行目の高さと、余白を当てる前のその縦の真ん中 (部品の器の上端から)。 */
type ControlLine = { height: number; center: number }

/**
 * 部品の 1 行目 (`alignGridRows` が行の中でそろえる単位)。React mock の `controlLineOf` と同じ。
 *
 * - 1 行の部品 (見えている中身の上端〜下端が 48px 以内): 中身の上端〜下端 (40px の select・24px のラジオ)
 * - 折り返した部品: 器の上端にそろっている最初の要素 (`firstLineHeight`)。2 行目以降はその下に続く
 * - 測れない部品は undefined (寄せない)
 */
const controlLineOf = (control: HTMLElement): ControlLine | undefined => {
  const box = control.getBoundingClientRect()
  const pad = parseFloat(getComputedStyle(control).paddingTop) || 0
  let top = Number.POSITIVE_INFINITY
  let bottom = Number.NEGATIVE_INFINITY
  for (const element of control.querySelectorAll<HTMLElement>('*')) {
    const rect = element.getBoundingClientRect()
    if (rect.width === 0 || rect.height === 0) continue
    top = Math.min(top, rect.top)
    bottom = Math.max(bottom, rect.bottom)
  }
  if (top === Number.POSITIVE_INFINITY) return undefined
  if (bottom - top <= MAX_FIRST_LINE_PX)
    return { height: bottom - top, center: (top + bottom) / 2 - box.top - pad }
  const first = firstLineHeight(control)
  return first === 0 ? undefined : { height: first, center: first / 2 }
}

/**
 * ラベルと部品の 1 行目を、格子の行ごとに縦の中央でそろえる (部品の器・ラベルの上の余白)。
 * 行の高さ = その行の部品の 1 行目のいちばん高いもの (40px の select があれば 40px)。それに満たない部品
 * (ラジオ・チェックボックス) もラベルも、その真ん中に置く。React mock の `alignGridRows` と同じ
 * (2026-10-28 デザイナー「label 含め、縦センターにアライン揃えたい」。11/02 #12150 でスポットの入札一覧の
 * 「売/買」のラジオが隣の「ステータス」の select より上にずれていた指摘で dev にも入れた)。
 */
const alignGridRows = (grid: HTMLElement): void => {
  // 行の上端は、余白を当てる前に全部測る (当てても同じ行のマスは一緒に動くので、行の分け方は変わらない)
  const rows = new Map<
    number,
    Array<{ label: HTMLElement | null; control: HTMLElement }>
  >()
  for (const cell of grid.querySelectorAll<HTMLElement>(
    ':scope > [data-grid-cell]',
  )) {
    const control = cell.querySelector<HTMLElement>('[data-grid-align]')
    if (control === null) continue
    const top = Math.round(cell.getBoundingClientRect().top)
    const label = cell.querySelector<HTMLElement>('[data-grid-label]')
    rows.set(top, [...(rows.get(top) ?? []), { label, control }])
  }
  for (const pairs of rows.values()) {
    const lines = pairs.map((pair) => controlLineOf(pair.control))
    const labelHeights = pairs.map((pair) =>
      pair.label === null
        ? 0
        : parseFloat(getComputedStyle(pair.label).lineHeight) || 0,
    )
    const rowHeight = Math.max(
      0,
      ...lines.map((line) => line?.height ?? 0),
      ...labelHeights,
    )
    for (const [index, pair] of pairs.entries()) {
      const line = lines[index]
      pair.control.style.paddingTop =
        line === undefined
          ? ''
          : `${Math.max(0, rowHeight / 2 - line.center)}px`
      if (pair.label !== null)
        pair.label.style.paddingTop = `${Math.max(0, (rowHeight - (labelHeights[index] ?? 0)) / 2)}px`
    }
  }
}

/** 部品の最小幅 (min-content。折り返さない日付のまとまり・幅を決めた select)。 */
const minContentWidth = (control: HTMLElement): number => {
  const width = control.style.width
  control.style.width = 'min-content'
  const minWidth = control.offsetWidth
  control.style.width = width
  return minWidth
}

type Props = {
  items: readonly FilterGridItem[]
  /** 渡した順のまま並べる (既定は `pinAreaAndPeriodFirst` でエリア → 期間関連 → 残り)。 */
  keepOrder?: boolean
}

/**
 * 開いた条件のカードの中身。「ラベル | 部品」のマスを左 → 右、入らなければ次の行へ流す
 * (UI 辞書 toolbar-collapsible.md「開いた条件のカード」)。
 *
 * - 列の幅は等分 (`repeat(N, 1fr)`)。ラベルの列の幅はカードで 1 つ (いちばん長いラベル)
 * - 列の数 = 幅に入る数 (1 マス = ラベルの列 + 8px + 部品の最小幅) と項目数の小さい方。
 *   部品の最小幅は、折り返さない部品 (「<」日付「>」のまとまり等) の min-content と 200px の大きい方
 * - ラベルと部品の 1 行目は、行の中でいちばん高い 1 行目 (40px の select があれば 40px) の縦の中央に
 *   そろえる (マスは上端そろえ、ラベル・部品の器に上の余白。`alignGridRows`)。
 *   2026-10-26 デザイナー「各項目のラベルは、右にくる部品と縦位置センターアラインしたい」、
 *   2026-10-28「label 含め、縦センターにアライン揃えたい」
 * - 列の間 20px・行の間 12px。部品は 40px (md) のまま使う (32px にしない)
 * - エリアと期間関連は先頭に寄せる (`pinAreaAndPeriodFirst`)
 *
 * [DS差異] React mock の FilterGrid にある「列数ごとに実際に並べて高さを比べる」測定・
 * 幅の広い部品 (`isWide`) の 2 列化・従属項目は持ち込んでいない (必要になった画面で足す)。
 * 行ごとの縦の中央そろえ (`alignGridRows`) と、部品の min-content を列の数に入れる所だけ React mock と同じ。
 */
export const FilterGrid = ({
  items,
  keepOrder = false,
}: Props): React.ReactElement => {
  const ordered = keepOrder ? [...items] : pinAreaAndPeriodFirst(items)
  const [grid, setGrid] = useState<HTMLElement | null>(null)
  const [labelWidth, setLabelWidth] = useState(0)
  const [columns, setColumns] = useState(1)
  const itemCount = ordered.length

  useLayoutEffect(() => {
    if (grid === null) return undefined
    const sizer = grid.querySelector<HTMLElement>('[data-grid-sizer]')
    const measure = (): void => {
      // 閉じている間 (hidden で幅 0) は測らない
      if (grid.clientWidth === 0) return
      const nextLabelWidth = sizer === null ? 0 : Math.ceil(sizer.offsetWidth)
      const controls = [
        ...grid.querySelectorAll<HTMLElement>('[data-grid-control]'),
      ]
      const minControlWidth = Math.max(
        MIN_CONTROL_WIDTH_PX,
        ...controls.map(minContentWidth),
      )
      const cellWidth = nextLabelWidth + LABEL_GAP_PX + minControlWidth
      const byWidth = Math.max(
        1,
        Math.floor(
          (grid.clientWidth + COLUMN_GAP_PX) / (cellWidth + COLUMN_GAP_PX),
        ),
      )
      const nextColumns = Math.max(1, Math.min(byWidth, itemCount))
      setLabelWidth((current) =>
        current === nextLabelWidth ? current : nextLabelWidth,
      )
      setColumns((current) => (current === nextColumns ? current : nextColumns))
      alignGridRows(grid)
    }
    measure()
    if (typeof ResizeObserver === 'undefined') return undefined
    const observer = new ResizeObserver(measure)
    observer.observe(grid)
    if (sizer !== null) observer.observe(sizer)
    return () => observer.disconnect()
    // 列の数・ラベルの幅が変わった後 (部品の折り返しが変わる) にも測り直す
  }, [grid, itemCount, columns, labelWidth])

  return (
    <Box
      ref={setGrid}
      position="relative"
      alignItems="start"
      rowGap={`${ROW_GAP_PX}px`}
      columnGap={`${COLUMN_GAP_PX}px`}
      gridTemplateColumns={`repeat(${columns}, minmax(0, 1fr))`}
      display="grid"
      width="100%"
      minWidth={0}
      padding={3}
      data-columns={columns}
    >
      {/* ラベルの列の幅を測る見えない箱 (全ラベルを縦に積む。幅 = いちばん長いラベル) */}
      <Box
        position="absolute"
        top={0}
        left={0}
        overflow="hidden"
        width="max-content"
        height={0}
        visibility="hidden"
        pointerEvents="none"
        aria-hidden
        data-grid-sizer=""
      >
        {ordered.map(
          (item) =>
            item.label !== undefined && (
              <Text key={item.key} textStyle="filterLabel" whiteSpace="nowrap">
                {item.label}
              </Text>
            ),
        )}
      </Box>
      {ordered.map((item) => (
        <Box
          key={item.key}
          // 横に並ぶマスは上端にそろえ、ラベルと部品は行の 1 行目の真ん中 (上の測定で付ける上の余白)
          alignItems="start"
          columnGap={`${LABEL_GAP_PX}px`}
          gridTemplateColumns={
            item.label === undefined
              ? 'minmax(0, 1fr)'
              : `${labelWidth}px minmax(0, 1fr)`
          }
          display="grid"
          minWidth={0}
          data-grid-cell={item.key}
        >
          {item.label !== undefined && (
            <Text
              textStyle="filterLabel"
              color="Text.highEmphasis"
              whiteSpace="nowrap"
              data-grid-label=""
            >
              {item.label}
            </Text>
          )}
          <Box
            justifySelf="start"
            minWidth={0}
            maxWidth="100%"
            data-grid-align=""
            {...(item.label !== undefined && { 'data-grid-control': '' })}
          >
            {item.control}
          </Box>
        </Box>
      ))}
    </Box>
  )
}
